// @flow

import React, { Fragment } from "react";
import { localize } from "redux-i18n";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Input,
  Col,
  Label,
  FormGroup,
  GSGrid,
} from "@components";
import type {
  ShiftPattern,
  UnassignReassignShiftPatternModalType,
  UnassignReassignShiftPatternCauseType,
  EmployeePositionsAssignedToShiftPattern,
  DepartmentsAssignedToShiftPattern,
} from "@modules/service/reducer/shift-patterns/types";
import {
  UnassignReassignShiftPatternTypeEnum,
  UnassignReassignShiftPatternCauseEnum,
} from "@modules/service/constants";
import { ReassignItemsToShiftPatternModal } from "../";

type OwnProps = $Exact<{
  isOpen: boolean,
  closeModal: Function,
  onSubmit: Function,
  shiftPattern: ?ShiftPattern,
  modalType: ?UnassignReassignShiftPatternModalType,
  employees: ?Array<EmployeePositionsAssignedToShiftPattern>,
  isReassignItemsToShiftPatternModalVisible: boolean,
  toggleUnassignReassignItemsToShiftPatternModal: Function,
  departments: ?Array<DepartmentsAssignedToShiftPattern>,
  unassignReassignItemsShiftPattern: Function,
  clientId: ?number,
  shiftPatternsList: ShiftPattern[],
}>;

type ComponentState = $Exact<{
  selected: number[],
  cause: UnassignReassignShiftPatternCauseType,
}>;

type Props = {
  ...PropsFromLocalize,
  ...OwnProps,
};

export class UnassignReassignShiftPatternModal extends React.Component<Props, ComponentState> {
  state = {
    selected: [],
    cause: UnassignReassignShiftPatternCauseEnum.UNASSIGN,
  };

  onToggleReassignItemsToShiftPatternModal = (visible: boolean) => () => {
    this.props.toggleUnassignReassignItemsToShiftPatternModal(visible);
  };

  onUnassignReassignButtonlick = (cause: UnassignReassignShiftPatternCauseType) => () => {
    this.setState(
      {
        cause,
      },
      this.onToggleReassignItemsToShiftPatternModal(true),
    );
  };

  get modalContent(): Object {
    const shiftPatternName = (this.props.shiftPattern || {}).name;
    return this.props.modalType === UnassignReassignShiftPatternTypeEnum.EMPLOYEES
      ? {
          modalTitle: this.props.t("EMPLOYEES_FOR_PATTERN", {
            patternName: shiftPatternName,
          }),
          tableData: this.employeesData,
          tableCols: this.emloyeesCols,
          reassignModalName: this.props.t("REASSIGN_EMPLOYEES"),
          reassignModalDescription: this.props.t(
            [
              "REASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN",
              "REASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN_plural",
              "employeesCount",
            ],
            {
              employeesCount: this.state.selected.length,
              shiftPatternName,
            },
          ),
          unassignModalName: this.props.t("UNASSIGN_EMPLOYEES"),
          unassignModalDescription: this.props.t(
            [
              "UNASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN",
              "UNASSIGN_EMPLOYEES_FROM_SHIFT_PATTERN_plural",
              "employeesCount",
            ],
            {
              employeesCount: this.state.selected.length,
              shiftPatternName,
            },
          ),
        }
      : {
          modalTitle: this.props.t("DEPARTMENTS_FOR_PATTERN", {
            patternName: shiftPatternName,
          }),
          tableData: this.props.departments,
          tableCols: this.departmentCols,
          reassignModalName: this.props.t("REASSIGN_DEPARTMENTS"),
          reassignModalDescription: this.props.t(
            [
              "REASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN",
              "REASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN_plural",
              "departmentsCount",
            ],
            {
              departmentsCount: this.state.selected.length,
              shiftPatternName,
            },
          ),
          unassignModalName: this.props.t("UNASSIGN_DEPARTMENTS"),
          unassignModalDescription: this.props.t(
            [
              "UNASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN",
              "UNASSIGN_DEPARTMENTS_FROM_SHIFT_PATTERN_plural",
              "departmentsCount",
            ],
            {
              departmentsCount: this.state.selected.length,
              shiftPatternName,
            },
          ),
        };
  }

  get employeesData() {
    return (this.props.employees || []).map(employee => {
      return {
        ...employee,
        fullName: `${employee.firstName} ${employee.lastName}`,
        department: employee.organisationalUnit.name,
      };
    });
  }

  get emloyeesCols() {
    return [
      { key: "employeeReference", name: this.props.t("EMPLOYEE_REF"), align: "left" },
      { key: "fullName", name: this.props.t("EMPLOYEE_NAME") },
      { key: "department", name: this.props.t("DEPARTMENT") },
    ];
  }

  get departmentCols() {
    return [
      { key: "id", name: this.props.t("DEPARTMENT_REF"), width: 140 },
      { key: "name", name: this.props.t("DEPARTMENT") },
    ];
  }

  onSelect = (selectedItems: number[]) => {
    this.setState({
      selected: selectedItems,
    });
  };

  onSelectAll = () =>
    this.setState({
      selected:
        this.state.selected.length === this.modalContent.tableData.length
          ? []
          : this.modalContent.tableData.map(item => item.id),
    });

  actionHandler = (newShiftPatternId: ?number) => () => {
    const update = newShiftPatternId ? { newShiftPatternId } : {};
    this.props.unassignReassignItemsShiftPattern({
      clientId: this.props.clientId,
      successMessage: newShiftPatternId ? "SUCCESSFULLY_REASSIGNED" : "SUCCESSFULLY_UNASSIGNED",
      data: {
        ids: [...this.state.selected],
        ...update,
      },
    });
    this.setState({
      selected: [],
    });
  };

  render() {
    return (
      <Fragment>
        {this.props.isReassignItemsToShiftPatternModalVisible && (
          <ReassignItemsToShiftPatternModal
            modalTitle={
              this.state.cause === UnassignReassignShiftPatternCauseEnum.UNASSIGN
                ? this.modalContent.unassignModalName
                : this.modalContent.reassignModalName
            }
            modalDescription={
              this.state.cause === UnassignReassignShiftPatternCauseEnum.UNASSIGN
                ? this.modalContent.unassignModalDescription
                : this.modalContent.reassignModalDescription
            }
            toggleModal={this.onToggleReassignItemsToShiftPatternModal}
            cause={this.state.cause}
            actionHandler={this.actionHandler}
            selectedShiftPatternId={(this.props.shiftPattern || {}).id}
            shiftPatternsList={this.props.shiftPatternsList}
          />
        )}
        {this.props.isOpen && (
          <Modal className="modal-large">
            <ModalHeader toggle={this.props.closeModal} className="d-flex justify-content-between">
              {this.modalContent.modalTitle}
            </ModalHeader>
            <ModalBody>
              <Col sm={{ size: 6, offset: 3 }}>
                <FormGroup>
                  <Label for="shiftPattern">{this.props.t("SHIFT_PATTERN_FIELD_LABEL_NAME")}</Label>
                  <Input id="shiftPattern" readOnly value={(this.props.shiftPattern || {}).name} />
                </FormGroup>
              </Col>
              <GSGrid
                rows={this.modalContent.tableData}
                columns={this.modalContent.tableCols}
                selectedRows={this.state.selected}
                onSelectedRowsChange={this.onSelect}
              />
            </ModalBody>
            <ModalFooter className="d-flex justify-content-end gap-1">
              <Button color="secondary" onClick={this.props.closeModal}>
                {this.props.t("CANCEL")}
              </Button>
              <Button
                onClick={this.onUnassignReassignButtonlick(
                  UnassignReassignShiftPatternCauseEnum.UNASSIGN,
                )}
                disabled={!this.state.selected.length}
              >
                {this.props.t("UNASSIGN_SELECTED")}
              </Button>
              {this.props.shiftPatternsList.length > 1 ? (
                <Button
                  onClick={this.onUnassignReassignButtonlick(
                    UnassignReassignShiftPatternCauseEnum.REASSIGN,
                  )}
                  disabled={!this.state.selected.length}
                >
                  {this.props.t("REASSIGN_SELECTED")}
                </Button>
              ) : null}
            </ModalFooter>
          </Modal>
        )}
      </Fragment>
    );
  }
}

export default localize()(UnassignReassignShiftPatternModal);
